<script>
  import { onMount } from 'svelte';
  import { getApiBase } from './apiBase.js';

  export let studentId = null;
  export let embedded = false;
  const apiBase = getApiBase();

  let students = [];
  let selectedStudent = null;
  let studentDetails = null;
  let studentOverview = null;
  let studentComments = null;
  let loading = false;
  let loadingOverview = false;
  let loadingComments = false;
  let error = '';
  let editMode = false;
  let saving = false;
  let studentQuery = '';
  let filteredStudents = [];
  let showStudentDropdown = false;
  let expandedComments = new Set();
  let showAttendanceDetails = false;
  
  // Sorted attendance details
  let sortedLateClasses = [];
  let sortedOnTimeClasses = [];
  let sortedNoShowClasses = [];
  
  // Phone search functionality
  let phoneQuery = '';
  let filteredPhones = [];
  let showPhoneDropdown = false;
  let searchType = 'name'; // 'name' or 'phone'

  // All transactions functionality
  let showAllTransactionsModal = false;
  let allTransactions = null;
  let loadingAllTransactions = false;

  // Month selection
  let selectedMonth = new Date().getMonth() + 1; // 1-12
  let selectedYear = new Date().getFullYear();
  let availableYears = [];

  // Editable student data
  let editableData = {
    user: {},
    google_cal_searchable: '',
    parent: {},
    phone_number: '',
    special_conditions: {}
  };

  let lastAutoSelectedStudentId = null;

  onMount(async () => {
    await loadStudents();
    initializeAvailableYears();
    if (embedded && studentId) {
      await autoSelectStudentById(studentId);
    }
  });

  async function autoSelectStudentById(id) {
    if (!id) return;
    const idNum = typeof id === 'string' ? parseInt(id, 10) : id;
    if (Number.isNaN(idNum)) return;
    if (!students || students.length === 0) return;
    if (idNum === lastAutoSelectedStudentId) return;

    const match = students.find(s => s.id === idNum);
    if (!match) {
      error = `Student with ID ${idNum} not found.`;
      return;
    }

    lastAutoSelectedStudentId = idNum;
    await selectStudent(match);
  }

  $: if (embedded && studentId && students.length > 0) {
    const idNum = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
    if (!Number.isNaN(idNum) && idNum !== lastAutoSelectedStudentId) {
      autoSelectStudentById(idNum);
    }
  }

  function initializeAvailableYears() {
    const currentYear = new Date().getFullYear();
    // Show current year and previous 2 years
    availableYears = [currentYear, currentYear - 1, currentYear - 2];
  }

  async function loadStudents() {
    try {
      const token = localStorage.getItem('jwtToken');
      const url = `${apiBase}/studentplayground/api/students-list/`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          students = data.students || [];
          filteredStudents = students;
        } else {
          throw new Error(data.message || 'Failed to load students');
        }
      } else {
        if (response.status === 401) {
          alert('Reikia prisijungti iš naujo.');
          localStorage.removeItem('jwtToken');
          window.location.href = '#/about';
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
    } catch (err) {
      console.error('Error loading students:', err);
      error = `Error loading students: ${err.message}`;
    }
  }

  async function selectStudent(student) {
    selectedStudent = student;
    studentQuery = student.name;
    showStudentDropdown = false;
    studentDetails = null;
    studentOverview = null;
    studentComments = null;
    editMode = false;
    loading = true;
    error = '';
    showAttendanceDetails = false;
    
    filteredStudents = students;

    await loadStudentData();
  }

  async function loadStudentData() {
    if (!selectedStudent) return;
    
    loading = true;
    error = '';
    
    // Clear old data to force refresh
    studentOverview = null;
    studentComments = null;
    showAttendanceDetails = false;
    sortedLateClasses = [];
    sortedOnTimeClasses = [];
    sortedNoShowClasses = [];

    try {
      const token = localStorage.getItem('jwtToken');
      
      // Build query parameters for month/year
      const monthParam = `?month=${selectedMonth}&year=${selectedYear}`;
      
      console.log(`Loading student data for ${getMonthName(selectedMonth)} ${selectedYear}`, monthParam);
      
      // Fetch detailed info, overview, and comments in parallel
      const detailsUrl = `${apiBase}/studentplayground/api/student-detailed-info/${selectedStudent.id}/`;
      const overviewUrl = `${apiBase}/studentplayground/api/student-overview/${selectedStudent.id}${monthParam}`;
      const commentsUrl = `${apiBase}/studentplayground/api/student-comments/${selectedStudent.id}${monthParam}`;
      const [detailsResponse, overviewResponse, commentsResponse] = await Promise.all([
        fetch(detailsUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(overviewUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(commentsUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (detailsResponse.ok) {
        const data = await detailsResponse.json();
        if (data.status === 'success') {
          studentDetails = data;
          initializeEditableData(data);
        } else {
          throw new Error(data.message || 'Failed to load student details');
        }
      } else {
        if (detailsResponse.status === 401) {
          alert('Reikia prisijungti iš naujo.');
          localStorage.removeItem('jwtToken');
          window.location.href = '#/about';
        } else {
          throw new Error(`HTTP error! status: ${detailsResponse.status}`);
        }
      }

      if (overviewResponse.ok) {
        const overviewData = await overviewResponse.json();
        if (overviewData.status === 'success') {
          console.log('Student overview data received:', {
            total_classes: overviewData.classes?.total_classes,
            late_count: overviewData.classes?.late_classes_details?.length,
            on_time_count: overviewData.classes?.on_time_classes_details?.length,
            no_show_count: overviewData.classes?.no_show_classes_details?.length
          });
          studentOverview = overviewData;
        } else {
          console.warn('Failed to load student overview:', overviewData.message);
        }
      } else {
        console.warn('Failed to fetch student overview');
      }

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json();
        if (commentsData.status === 'success') {
          console.log('Student comments data received:', {
            comments_count: commentsData.comments_count,
            comments_length: commentsData.comments?.length
          });
          studentComments = commentsData;
        } else {
          console.warn('Failed to load student comments:', commentsData.message);
        }
      } else {
        console.warn('Failed to fetch student comments');
      }

    } catch (err) {
      console.error('Error loading student data:', err);
      error = `Error loading student data: ${err.message}`;
    } finally {
      loading = false;
    }
  }

  function filterStudents() {
    if (!studentQuery.trim()) {
      filteredStudents = students;
      showStudentDropdown = false;
    } else {
      const query = studentQuery.toLowerCase();
      filteredStudents = students.filter(student => 
        student.name.toLowerCase().includes(query) ||
        student.email.toLowerCase().includes(query)
      );
      showStudentDropdown = !selectedStudent && filteredStudents.length > 0;
    }
  }

  // Handle month/year change
  async function handleMonthChange() {
    if (selectedStudent) {
      await loadStudentData();
    }
  }

  // Get month name for display
  function getMonthName(monthNum) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNum - 1];
  }

  function initializeEditableData(data) {
    editableData = {
      user: { ...data.user },
      google_cal_searchable: data.google_cal_searchable || '',
      parent: { ...data.parent },
      phone_number: data.phone_number || '',
      special_conditions: { ...data.special_conditions }
    };
  }

  function toggleEditMode() {
    if (editMode) {
      if (studentDetails) {
        initializeEditableData(studentDetails);
      }
    }
    editMode = !editMode;
    error = '';
  }

  async function saveStudentInfo() {
    if (!selectedStudent) {
      error = 'No student selected';
      return;
    }

    saving = true;
    error = '';

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${apiBase}/studentplayground/api/update-student-detailed-info/${selectedStudent.id}/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editableData)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          studentDetails = data;
          initializeEditableData(data);
          editMode = false;
          alert('Student information updated successfully!');
        } else {
          throw new Error(data.message || 'Failed to save student information');
        }
      } else {
        if (response.status === 401) {
          alert('Reikia prisijungti iš naujo.');
          localStorage.removeItem('jwtToken');
          window.location.href = '#/about';
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
    } catch (err) {
      console.error('Error saving student information:', err);
      error = `Error saving student information: ${err.message}`;
    } finally {
      saving = false;
    }
  }

  function toggleCommentExpansion(commentId) {
    if (expandedComments.has(commentId)) {
      expandedComments.delete(commentId);
    } else {
      expandedComments.add(commentId);
    }
    expandedComments = expandedComments; // Trigger reactivity
  }

  // Function to sort attendance details by scheduled time
  function sortAttendanceByTime(attendanceArray) {
    if (!attendanceArray || !Array.isArray(attendanceArray)) {
      return [];
    }
    
    return [...attendanceArray].sort((a, b) => {
      // Compare scheduled_time strings directly - they should be in a comparable format
      if (!a.scheduled_time || !b.scheduled_time) {
        return 0;
      }
      
      try {
        const timeA = parseDateTime(a.scheduled_time);
        const timeB = parseDateTime(b.scheduled_time);
        if (timeA && timeB) {
          return timeA.getTime() - timeB.getTime();
        } else {
          // Fallback to string comparison if date parsing fails
          return a.scheduled_time.localeCompare(b.scheduled_time);
        }
      } catch (error) {
        // Fallback to string comparison if date parsing fails
        return a.scheduled_time.localeCompare(b.scheduled_time);
      }
    });
  }

  // Helper function to parse datetime and return a Date object for Lithuanian timezone formatting
  function parseDateTime(datetime) {
    if (!datetime) return null;
    
    try {
      let date;
      
      if (datetime.includes('T')) {
        // ISO format datetime - handle various formats
        if (datetime.endsWith('Z')) {
          // Handle non-standard format like "2025-09-10T090000Z"
          if (/T\d{6}Z$/.test(datetime)) {
            // Convert HHMMSS to HH:MM:SS format
            const match = datetime.match(/^(.+T)(\d{2})(\d{2})(\d{2})(Z)$/);
            if (match) {
              const formattedDateTime = `${match[1]}${match[2]}:${match[3]}:${match[4]}${match[5]}`;
              date = new Date(formattedDateTime);
            } else {
              date = new Date(datetime);
            }
          } else {
            date = new Date(datetime);
          }
        } else {
          // If no Z suffix, assume it's UTC time without the Z indicator
          date = new Date(datetime + 'Z');
        }
      } else {
        // Space-separated format, assume UTC
        const parts = datetime.split(" ");
        if (parts.length >= 2) {
          // Handle format like "2025-09-10 09:00"
          const isoString = `${parts[0]}T${parts[1]}Z`;
          date = new Date(isoString);
        } else {
          // Fallback for partial datetime
          date = new Date(datetime);
        }
      }
      
      return date;
      
    } catch (error) {
      console.warn('Error parsing datetime:', datetime, error);
      return new Date(datetime); // Fallback to original
    }
  }

  // Format datetime with Lithuanian timezone conversion
  function formatDateTime(datetime) {
    if (!datetime) return '';
    
    try {
      const date = parseDateTime(datetime);
      if (!date || isNaN(date.getTime())) {
        console.warn('formatDateTime: Failed to parse date, returning original:', datetime);
        return datetime;
      }
      
      // Use manual timezone conversion for better browser compatibility
      const lithuanianTime = new Date(date.getTime() + getTimezoneOffset());
      
      const year = lithuanianTime.getUTCFullYear();
      const month = (lithuanianTime.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = lithuanianTime.getUTCDate().toString().padStart(2, '0');
      const hours = lithuanianTime.getUTCHours().toString().padStart(2, '0');
      const minutes = lithuanianTime.getUTCMinutes().toString().padStart(2, '0');
      
      // Get day of week in Lithuanian
      const dayNames = ['Sek', 'Pir', 'Ant', 'Tre', 'Ket', 'Pen', 'Šeš'];
      const dayName = dayNames[lithuanianTime.getUTCDay()];
      
      return `${dayName} ${year}-${month}-${day} ${hours}:${minutes}`;
      
    } catch (error) {
      console.warn('Error formatting datetime:', datetime, error);
      return datetime;
    }
  }

  // Format time only (HH:MM) with Lithuanian timezone conversion
  function formatTimeOnly(datetime) {
    if (!datetime) return '';
    
    try {
      const date = parseDateTime(datetime);
      if (!date || isNaN(date.getTime())) {
        console.warn('formatTimeOnly: Failed to parse date or invalid date, using fallback parsing for:', datetime);
        return extractTimeWithTimezone(datetime);
      }
      
      // Use manual timezone conversion for better browser compatibility
      const lithuanianTime = new Date(date.getTime() + getTimezoneOffset());
      const hours = lithuanianTime.getUTCHours().toString().padStart(2, '0');
      const minutes = lithuanianTime.getUTCMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
      
    } catch (error) {
      console.warn('Error formatting time:', datetime, error);
      return extractTimeWithTimezone(datetime);
    }
  }
  
  // Helper function to get Lithuanian timezone offset in milliseconds
  function getTimezoneOffset() {
    // Lithuania is UTC+2 in winter, UTC+3 in summer (DST)
    // Use a simple approximation based on current date
    const now = new Date();
    const year = now.getFullYear();
    
    // DST in Lithuania: last Sunday in March to last Sunday in October
    const dstStart = getLastSundayOfMonth(year, 2); // March (0-indexed)
    const dstEnd = getLastSundayOfMonth(year, 9);   // October (0-indexed)
    
    if (now >= dstStart && now < dstEnd) {
      return 3 * 60 * 60 * 1000; // UTC+3 (summer time)
    } else {
      return 2 * 60 * 60 * 1000; // UTC+2 (standard time)
    }
  }
  
  // Helper function to get the last Sunday of a given month
  function getLastSundayOfMonth(year, month) {
    const lastDay = new Date(year, month + 1, 0); // Last day of the month
    const lastSunday = new Date(lastDay);
    lastSunday.setDate(lastDay.getDate() - lastDay.getDay()); // Go back to last Sunday
    lastSunday.setHours(1, 0, 0, 0); // Set to 1 AM to avoid midnight edge cases
    return lastSunday;
  }
  
  // Fallback function to extract time and convert timezone
  function extractTimeWithTimezone(datetime) {
    if (typeof datetime === 'string') {
      // Extract time from formats like "2025-09-10 09:00" or "2025-09-10T09:00:00"
      const timeMatch = datetime.match(/(\d{2}):(\d{2})/);
      if (timeMatch) {
        const hours = parseInt(timeMatch[1]);
        const minutes = timeMatch[2];
        
        // Determine if we're in DST period (approximation)
        const now = new Date();
        const isDST = now.getMonth() >= 2 && now.getMonth() <= 9; // Rough DST period
        const offset = isDST ? 3 : 2; // UTC+3 in summer, UTC+2 in winter
        
        const lithuanianHours = (hours + offset) % 24;
        return `${lithuanianHours.toString().padStart(2, '0')}:${minutes}`;
      }
    }
    
    return datetime; // Ultimate fallback to original
  }

  // Format date only with Lithuanian timezone conversion
  function formatDateOnly(datetime) {
    if (!datetime) return '';
    
    try {
      const date = parseDateTime(datetime);
      if (!date || isNaN(date.getTime())) {
        console.warn('formatDateOnly: Failed to parse date, returning original:', datetime);
        return datetime;
      }
      
      // Use manual timezone conversion for better browser compatibility
      const lithuanianTime = new Date(date.getTime() + getTimezoneOffset());
      
      const year = lithuanianTime.getUTCFullYear();
      const month = (lithuanianTime.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = lithuanianTime.getUTCDate().toString().padStart(2, '0');
      
      return `${year}-${month}-${day}`;
      
    } catch (error) {
      console.warn('Error formatting date:', datetime, error);
      return datetime;
    }
  }

  // Format compact date and time for attendance display (MM-DD HH:MM)
  function formatCompactDateTime(datetime) {
    if (!datetime) return '';
    
    try {
      const date = parseDateTime(datetime);
      if (!date || isNaN(date.getTime())) {
        console.warn('formatCompactDateTime: Failed to parse date, using fallback for:', datetime);
        return extractTimeWithTimezone(datetime);
      }
      
      // Use manual timezone conversion for better browser compatibility
      const lithuanianTime = new Date(date.getTime() + getTimezoneOffset());
      
      const month = (lithuanianTime.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = lithuanianTime.getUTCDate().toString().padStart(2, '0');
      const hours = lithuanianTime.getUTCHours().toString().padStart(2, '0');
      const minutes = lithuanianTime.getUTCMinutes().toString().padStart(2, '0');
      
      return `${month}-${day} ${hours}:${minutes}`;
      
    } catch (error) {
      console.warn('Error formatting compact datetime:', datetime, error);
      return extractTimeWithTimezone(datetime);
    }
  }

  async function searchPhoneNumbers() {
    if (!phoneQuery.trim()) {
      filteredPhones = [];
      showPhoneDropdown = false;
      return;
    }

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${apiBase}/studentplayground/api/students-list/?search=${encodeURIComponent(phoneQuery)}&type=phone`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.search_type === 'phone') {
          filteredPhones = data.phones || [];
          showPhoneDropdown = filteredPhones.length > 0;
        }
      } else {
        if (response.status === 401) {
          alert('Reikia prisijungti iš naujo.');
          localStorage.removeItem('jwtToken');
          window.location.href = '#/about';
        }
      }
    } catch (err) {
      console.error('Error searching phone numbers:', err);
    }
  }

  async function selectStudentByPhone(phoneItem) {
    // Find the student by ID
    const token = localStorage.getItem('jwtToken');
    
    try {
      const response = await fetch(`${apiBase}/studentplayground/api/students-list/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          const student = data.students.find(s => s.id === phoneItem.student_id);
          if (student) {
            // Copy exact same logic as selectStudent for name search
            selectedStudent = student;
            phoneQuery = phoneItem.phone;
            showPhoneDropdown = false;
            studentDetails = null;
            studentOverview = null;
            studentComments = null;
            editMode = false;
            loading = true;
            error = '';
            showAttendanceDetails = false;
            
            // Clear name search
            studentQuery = '';
            showStudentDropdown = false;
            filteredStudents = students;

            // Load student data using the new function
            await loadStudentData();
          }
        }
      }
    } catch (err) {
      console.error('Error selecting student by phone:', err);
    }
  }

  function switchSearchType(type) {
    searchType = type;
    
    // Clear the other search
    if (type === 'name') {
      phoneQuery = '';
      showPhoneDropdown = false;
      filteredPhones = [];
    } else {
      studentQuery = '';
      showStudentDropdown = false;
      filteredStudents = students;
    }
    
    // Clear selection if switching types
    if (selectedStudent) {
      selectedStudent = null;
      studentDetails = null;
      studentOverview = null;
      studentComments = null;
      expandedComments = new Set();
      editMode = false;
      showAttendanceDetails = false;
    }
  }

  async function fetchAllTransactions() {
    if (!selectedStudent) {
      return;
    }

    loadingAllTransactions = true;
    
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${apiBase}/studentplayground/api/student-all-transactions/${selectedStudent.id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          allTransactions = data;
          showAllTransactionsModal = true;
        } else {
          throw new Error(data.message || 'Failed to load all transactions');
        }
      } else {
        if (response.status === 401) {
          alert('Reikia prisijungti iš naujo.');
          localStorage.removeItem('jwtToken');
          window.location.href = '#/about';
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
    } catch (err) {
      console.error('Error loading all transactions:', err);
      alert(`Error loading all transactions: ${err.message}`);
    } finally {
      loadingAllTransactions = false;
    }
  }

  function closeAllTransactionsModal() {
    showAllTransactionsModal = false;
    allTransactions = null;
  }

  // Reactive statement to filter students when search query changes
  $: if (studentQuery !== undefined) {
    filterStudents();
  }

  // Reactive statement for phone search
  $: if (phoneQuery !== undefined && searchType === 'phone') {
    searchPhoneNumbers();
  }

  // Reactive statements to sort attendance details when data changes
  $: if (studentOverview?.classes?.late_classes_details) {
    sortedLateClasses = sortAttendanceByTime(studentOverview.classes.late_classes_details);
  }

  $: if (studentOverview?.classes?.on_time_classes_details) {
    sortedOnTimeClasses = sortAttendanceByTime(studentOverview.classes.on_time_classes_details);
  }

  $: if (studentOverview?.classes?.no_show_classes_details) {
    sortedNoShowClasses = sortAttendanceByTime(studentOverview.classes.no_show_classes_details);
  }
</script>

<div class="student-info-container {embedded ? 'embedded' : ''}">
  <h3>Student Information Management</h3>
  
  {#if error}
    <div class="error-message">
      {error}
    </div>
  {/if}

  {#if !embedded}
  <!-- Student Selection -->
  <div class="selection-section">
    <h4>Step 1: Select Student</h4>
    
    <!-- Search Type Toggle -->
    <div class="search-type-toggle">
      <button 
        type="button" 
        class="toggle-btn {searchType === 'name' ? 'active' : ''}"
        on:click={() => switchSearchType('name')}
      >
        🔍 Search by Name
      </button>
      <button 
        type="button" 
        class="toggle-btn {searchType === 'phone' ? 'active' : ''}"
        on:click={() => switchSearchType('phone')}
      >
        📱 Search by Phone
      </button>
    </div>

    <!-- Name Search -->
    {#if searchType === 'name'}
      <div class="input-container">
        <input 
          type="text" 
          placeholder="Search student by name..."
          bind:value={studentQuery}
          on:input={() => {
            if (selectedStudent && studentQuery !== selectedStudent.name) {
              selectedStudent = null;
              showStudentDropdown = true;
            } else if (!selectedStudent) {
              showStudentDropdown = studentQuery.trim().length > 0;
            }
          }}
          autocomplete="off"
          class="search-input"
        />
        
        {#if showStudentDropdown}
          <ul class="autocomplete-list">
            {#each filteredStudents.slice(0, 8) as student}
              <li>
                <button
                  type="button"
                  on:click={() => selectStudent(student)}
                  on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectStudent(student)}
                >
                  <div class="student-option">
                    <span class="student-name">{student.name}</span>
                    {#if student.email}
                      <span class="student-email">{student.email}</span>
                    {/if}
                  </div>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {/if}

    <!-- Phone Search -->
    {#if searchType === 'phone'}
      <div class="input-container">
        <input 
          type="text" 
          placeholder="Search student by phone number..."
          bind:value={phoneQuery}
          on:input={() => {
            if (selectedStudent && phoneQuery !== selectedStudent.phone) {
              selectedStudent = null;
            }
          }}
          autocomplete="off"
          class="search-input"
        />
        
        {#if showPhoneDropdown}
          <ul class="autocomplete-list">
            {#each filteredPhones.slice(0, 8) as phoneItem}
              <li>
                <button
                  type="button"
                  on:click={() => selectStudentByPhone(phoneItem)}
                  on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectStudentByPhone(phoneItem)}
                >
                  <div class="phone-option">
                    <span class="phone-number">📱 {phoneItem.phone}</span>
                    <span class="phone-student-name">{phoneItem.student_name}</span>
                    {#if phoneItem.student_email}
                      <span class="phone-student-email">{phoneItem.student_email}</span>
                    {/if}
                  </div>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {/if}
    
    {#if selectedStudent}
      <div class="selected-student">
        <div class="selected-student-info">
          <h5>Selected Student</h5>
          <p><strong>Name:</strong> {selectedStudent.name}</p>
          {#if selectedStudent.email}
            <p><strong>Email:</strong> {selectedStudent.email}</p>
          {/if}
        </div>
        
        <button 
          type="button" 
          class="btn-clear-small" 
          on:click={() => { 
            selectedStudent = null; 
            studentQuery = ''; 
            phoneQuery = '';
            showStudentDropdown = false; 
            showPhoneDropdown = false;
            studentDetails = null;
            studentOverview = null;
            studentComments = null;
            expandedComments = new Set();
            editMode = false;
            showAttendanceDetails = false;
            searchType = 'name';
          }}
        >
          Clear Selection
        </button>
      </div>
    {/if}
  </div>
  {/if}

  <!-- Month Selection -->
  <div class="selection-section">
    <h4>Step 2: Select Month & Year</h4>
    <div class="month-selection">
      <div class="month-selector-container">
        <label for="month-select" class="selector-label">Month:</label>
        <select 
          id="month-select"
          bind:value={selectedMonth} 
          on:change={handleMonthChange}
          class="month-select"
        >
          <option value={1}>January</option>
          <option value={2}>February</option>
          <option value={3}>March</option>
          <option value={4}>April</option>
          <option value={5}>May</option>
          <option value={6}>June</option>
          <option value={7}>July</option>
          <option value={8}>August</option>
          <option value={9}>September</option>
          <option value={10}>October</option>
          <option value={11}>November</option>
          <option value={12}>December</option>
        </select>
      </div>
      
      <div class="month-selector-container">
        <label for="year-select" class="selector-label">Year:</label>
        <select 
          id="year-select"
          bind:value={selectedYear} 
          on:change={handleMonthChange}
          class="year-select"
        >
          {#each availableYears as year}
            <option value={year}>{year}</option>
          {/each}
        </select>
      </div>

      <div class="selected-period">
        <span class="period-label">Viewing data for:</span>
        <span class="period-value">{getMonthName(selectedMonth)} {selectedYear}</span>
      </div>
    </div>
  </div>

  <!-- Loading Indicator -->
  {#if loading}
    <div class="loading">
      Loading student details...
    </div>
  {/if}

  <!-- Student Overview Display -->
  {#if studentOverview && !loading}
    <div class="student-overview">
      <h4>Student Overview: {studentOverview.student_name} - {getMonthName(selectedMonth)} {selectedYear}</h4>
      
      <!-- Balance Information -->
      <div class="overview-section">
        <h5>💰 Balance Information</h5>
        <div class="overview-grid">
          <div class="overview-item">
            <span class="overview-label">Current Balance:</span>
            <span class="overview-value">€{studentOverview.balance.current_balance}</span>
          </div>
          <div class="overview-item">
            <span class="overview-label">Recent Transactions:</span>
            <span class="overview-value">{studentOverview.balance.recent_transactions_count}</span>
          </div>
        </div>
        
        {#if studentOverview.balance.recent_transactions.length > 0}
          <div class="transactions-list">
            <h6>Recent Transactions:</h6>
            {#each studentOverview.balance.recent_transactions as transaction}
              <div class="transaction-item">
                <span class="transaction-amount">€{transaction.amount}</span>
                <span class="transaction-type">{transaction.transaction_type}</span>
                <span class="transaction-date">{formatDateOnly(transaction.date)}</span>
              </div>
            {/each}
            
            <!-- View All Transactions Button -->
            <div class="view-all-transactions-btn-container">
              <button 
                type="button"
                class="btn-view-all-transactions"
                on:click={fetchAllTransactions}
                disabled={loadingAllTransactions}
              >
                {loadingAllTransactions ? 'Loading...' : '📋 View All Transactions'}
              </button>
            </div>
          </div>
        {/if}
      </div>

      <!-- Class Attendance -->
      <div class="overview-section">
        <h5>📚 Class Attendance</h5>
        <div class="overview-grid">
          <div class="overview-item">
            <span class="overview-label">Total Classes:</span>
            <span class="overview-value">{studentOverview.classes.total_classes}</span>
          </div>
          <div class="overview-item">
            <span class="overview-label">Attended:</span>
            <span class="overview-value">{studentOverview.classes.attended_classes}</span>
          </div>
          <div class="overview-item">
            <span class="overview-label">Attendance Rate:</span>
            <span class="overview-value">{studentOverview.classes.attendance_rate}%</span>
          </div>
          <div class="overview-item">
            <span class="overview-label">Late Attendances:</span>
            <span class="overview-value">{studentOverview.classes.late_attendance_count || 0}</span>
          </div>
        </div>
        
        <!-- Expandable Attendance Details -->
        {#if (sortedLateClasses && sortedLateClasses.length > 0) || (sortedNoShowClasses && sortedNoShowClasses.length > 0) || (sortedOnTimeClasses && sortedOnTimeClasses.length > 0)}
          <div class="attendance-details-toggle">
            <button 
              type="button"
              class="btn-toggle-details"
              on:click={() => showAttendanceDetails = !showAttendanceDetails}
            >
              {showAttendanceDetails ? '▼' : '▶'} View Attendance Details
            </button>
          </div>
          
          {#if showAttendanceDetails}
            <div class="attendance-details">
              
              <!-- Late Attendances -->
              {#if sortedLateClasses && sortedLateClasses.length > 0}
                <div class="attendance-category">
                  <h6>🕐 Late Attendances ({sortedLateClasses.length})</h6>
                  <div class="attendance-list">
                    {#each sortedLateClasses as lateClass}
                      <div class="attendance-item late-item">
                        <div class="attendance-header">
                          <span class="class-name">{lateClass.class_name}</span>
                          <span class="class-teacher">{lateClass.teacher}</span>
                          <span class="late-badge">{lateClass.minutes_late} min late</span>
                        </div>
                        <div class="attendance-times">
                          <span class="time-info">
                            <strong>Scheduled:</strong> {formatCompactDateTime(lateClass.scheduled_time)}
                          </span>
                          <span class="time-info">
                            <strong>Attended:</strong> {formatCompactDateTime(lateClass.attendance_time)}
                          </span>
                        </div>
                      </div>
                    {/each}
                  </div>
                </div>
              {/if}
              
              <!-- On-Time Attendances -->
              {#if sortedOnTimeClasses && sortedOnTimeClasses.length > 0}
                <div class="attendance-category">
                  <h6>✅ On-Time Attendances ({sortedOnTimeClasses.length})</h6>
                  <div class="attendance-list">
                    {#each sortedOnTimeClasses as onTimeClass}
                      <div class="attendance-item on-time-item">
                        <div class="attendance-header">
                          <span class="class-name">{onTimeClass.class_name}</span>
                          <span class="class-teacher">{onTimeClass.teacher}</span>
                          <span class="on-time-badge">
                            {onTimeClass.minutes_early > 0 ? `${onTimeClass.minutes_early} min early` : 'On Time'}
                          </span>
                        </div>
                        <div class="attendance-times">
                          <span class="time-info">
                            <strong>Scheduled:</strong> {formatCompactDateTime(onTimeClass.scheduled_time)}
                          </span>
                          <span class="time-info">
                            <strong>Attended:</strong> {formatCompactDateTime(onTimeClass.attendance_time)}
                          </span>
                        </div>
                      </div>
                    {/each}
                  </div>
                </div>
              {/if}
              
              <!-- No Shows -->
              {#if sortedNoShowClasses && sortedNoShowClasses.length > 0}
                <div class="attendance-category">
                  <h6>❌ No Shows ({sortedNoShowClasses.length})</h6>
                  <div class="attendance-list">
                    {#each sortedNoShowClasses as noShowClass}
                      <div class="attendance-item no-show-item">
                        <div class="attendance-header">
                          <span class="class-name">{noShowClass.class_name}</span>
                          <span class="class-teacher">{noShowClass.teacher}</span>
                          <span class="no-show-badge">No Show</span>
                        </div>
                        <div class="attendance-times">
                          <span class="time-info">
                            <strong>Scheduled:</strong> {formatCompactDateTime(noShowClass.scheduled_time)}
                          </span>
                        </div>
                      </div>
                    {/each}
                  </div>
                </div>
              {/if}
            </div>
          {/if}
        {/if}
      </div>

      <!-- Subjects -->
      <div class="overview-section">
        <h5>📖 Subjects</h5>
        <div class="subjects-overview">
          {#if studentOverview.subjects && studentOverview.subjects.length > 0}
            {#each studentOverview.subjects as subject}
              <span class="subject-tag">{subject}</span>
            {/each}
          {:else}
            <p class="no-data">No subjects found</p>
          {/if}
        </div>
      </div>

      <!-- Teachers -->
      <div class="overview-section">
        <h5>👨‍🏫 Teachers</h5>
        <div class="teachers-overview">
          {#each studentOverview.teachers as teacher}
            <div class="teacher-item">
              <span class="teacher-name">{teacher.name}</span>
              <small class="teacher-details">{teacher.class_count} classes</small>
            </div>
          {/each}
        </div>
      </div>
    </div>
  {/if}

  <!-- Student Comments Display -->
  {#if studentComments && !loading}
    <div class="student-comments">
      <h4>💬 Teacher Comments: {studentComments.student_name} - {getMonthName(selectedMonth)} {selectedYear}</h4>
      
      {#if studentComments.comments && studentComments.comments.length > 0}
        <div class="comments-summary">
          <p>Total Comments: <strong>{studentComments.comments_count}</strong></p>
          <p class="period-note">Comments from {getMonthName(selectedMonth)} {selectedYear}</p>
        </div>
        
        <div class="comments-list">
          {#each studentComments.comments as comment}
            <div class="comment-item">
              <div class="comment-header" on:click={() => toggleCommentExpansion(comment.id)} role="button" tabindex="0" on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleCommentExpansion(comment.id)}>
                <div class="comment-basic-info">
                  <span class="comment-class">{comment.class_name}</span>
                  <span class="comment-subject">{comment.subject}</span>
                  <span class="comment-teacher">{comment.teacher_name}</span>
                  <span class="comment-date">{formatDateTime(comment.displayTime)}</span>
                </div>
                <div class="comment-summary">
                  <span class="comment-note-preview">
                    {comment.note && comment.note.length > 50 ? comment.note.substring(0, 50) + '...' : comment.note || 'No note'}
                  </span>
                  <span class="expand-indicator">
                    {expandedComments.has(comment.id) ? '▼' : '▶'}
                  </span>
                </div>
              </div>
              
              {#if expandedComments.has(comment.id)}
                <div class="comment-details">
                  <div class="comment-ratings">
                    <div class="rating-item">
                      <span class="rating-label">Learned:</span>
                      <span class="rating-value">{comment.learned}/5</span>
                    </div>
                    <div class="rating-item">
                      <span class="rating-label">Technical Issues:</span>
                      <span class="rating-value">{comment.technical_issues}/5</span>
                    </div>
                    <div class="rating-item">
                      <span class="rating-label">Effort:</span>
                      <span class="rating-value">{comment.effort}/5</span>
                    </div>
                    <div class="rating-item">
                      <span class="rating-label">General:</span>
                      <span class="rating-value">{comment.general}/5</span>
                    </div>
                    <div class="rating-item">
                      <span class="rating-label">On Time:</span>
                      <span class="rating-value">{comment.on_time ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                  
                  {#if comment.note}
                    <div class="comment-full-note">
                      <h6>Full Comment:</h6>
                      <p>{comment.note}</p>
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
          {/each}
        </div>
      {:else}
        <div class="no-comments">
          <p>No teacher comments found for this student.</p>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Student Details Display -->
  {#if studentDetails && !loading}
    <div class="student-details">
      <div class="student-details-header">
        <h4>Student Details: {studentDetails.user.first_name} {studentDetails.user.last_name}</h4>
        <div class="edit-controls">
          <button class="btn btn-secondary" on:click={toggleEditMode}>
            {editMode ? 'Cancel Edit' : 'Edit Information'}
          </button>
          {#if editMode}
            <button class="btn btn-primary" on:click={saveStudentInfo} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          {/if}
        </div>
      </div>
      
      <!-- Basic Student Information -->
      <div class="info-section">
        <h5>Basic Information</h5>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">First Name:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.user.first_name} class="edit-input" />
            {:else}
              <span class="value">{studentDetails.user.first_name}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Last Name:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.user.last_name} class="edit-input" />
            {:else}
              <span class="value">{studentDetails.user.last_name}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Email:</span>
            {#if editMode}
              <input type="email" bind:value={editableData.user.email} class="edit-input" />
            {:else}
              <span class="value">{studentDetails.user.email}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Google Cal Searchable:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.google_cal_searchable} class="edit-input" />
            {:else}
              <span class="value">{studentDetails.google_cal_searchable || 'Not set'}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Phone Number:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.phone_number} class="edit-input" />
            {:else}
              <span class="value">{studentDetails.phone_number || 'Not set'}</span>
            {/if}
          </div>
        </div>
      </div>

      <!-- Parent Information -->
      <div class="info-section">
        <h5>Parent Information</h5>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">Parent Name:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.parent.name} class="edit-input" />
            {:else}
              <span class="value">{studentDetails.parent.name || 'Not set'}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Parent Email:</span>
            {#if editMode}
              <input type="email" bind:value={editableData.parent.email} class="edit-input" />
            {:else}
              <span class="value">{studentDetails.parent.email || 'Not set'}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Parent Phone:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.parent.phone} class="edit-input" />
            {:else}
              <span class="value">{studentDetails.parent.phone || 'Not set'}</span>
            {/if}
          </div>
        </div>
      </div>

      <!-- Special Conditions -->
      <div class="info-section">
        <h5>Special Conditions</h5>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">Conditions:</span>
            {#if editMode}
              <textarea bind:value={editableData.special_conditions.conditions} class="edit-textarea" rows="3"></textarea>
            {:else}
              <span class="value">{studentDetails.special_conditions.conditions || 'None'}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Notes:</span>
            {#if editMode}
              <textarea bind:value={editableData.special_conditions.notes} class="edit-textarea" rows="2"></textarea>
            {:else}
              <span class="value">{studentDetails.special_conditions.notes || 'None'}</span>
            {/if}
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- All Transactions Modal -->
  {#if showAllTransactionsModal && allTransactions}
    <div class="modal-overlay" on:click={closeAllTransactionsModal} role="button" tabindex="0" on:keydown={(e) => e.key === 'Escape' && closeAllTransactionsModal()}>
      <div class="modal-content" on:click|stopPropagation role="button" tabindex="0" on:keydown={() => {}}>
        <div class="modal-header">
          <h4>💰 All Transactions: {allTransactions.student_name}</h4>
          <button type="button" class="modal-close-btn" on:click={closeAllTransactionsModal}>×</button>
        </div>
        
        <div class="modal-body">
          <div class="transactions-summary">
            <p>Total Transactions: <strong>{allTransactions.transactions_count}</strong></p>
          </div>
          
          {#if allTransactions.transactions && allTransactions.transactions.length > 0}
            <div class="all-transactions-list">
              {#each allTransactions.transactions as transaction}
                <div class="transaction-item-detailed">
                  <div class="transaction-main-info">
                    <span class="transaction-amount-detailed {transaction.transaction_type === 'payment' ? 'payment' : 'charge'}">
                      €{transaction.amount}
                    </span>
                    <span class="transaction-type-detailed">{transaction.transaction_type}</span>
                    <span class="transaction-date-detailed">{formatDateOnly(transaction.date)}</span>
                  </div>
                  {#if transaction.description}
                    <div class="transaction-description">
                      <span class="description-label">Description:</span>
                      <span class="description-text">{transaction.description}</span>
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
          {:else}
            <div class="no-transactions">
              <p>No transactions found for this student.</p>
            </div>
          {/if}
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" on:click={closeAllTransactionsModal}>
            Close
          </button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .student-info-container {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .student-info-container.embedded {
    max-width: none;
    margin: 0;
    padding: 0;
  }

  .selection-section {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
  }

  .selection-section h4 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 1.1em;
  }

  .month-selection {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }

  .month-selector-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .selector-label {
    font-weight: 600;
    color: #555;
    font-size: 0.95em;
  }

  .month-select,
  .year-select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
    cursor: pointer;
    transition: border-color 0.2s;
  }

  .month-select {
    min-width: 130px;
  }

  .year-select {
    min-width: 90px;
  }

  .month-select:focus,
  .year-select:focus {
    outline: none;
    border-color: #28a745;
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
  }

  .month-select:hover,
  .year-select:hover {
    border-color: #28a745;
  }

  .selected-period {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #d4edda;
    border: 2px solid #28a745;
    border-radius: 6px;
    margin-left: auto;
  }

  .period-label {
    font-weight: 500;
    color: #666;
    font-size: 0.9em;
  }

  .period-value {
    font-weight: 600;
    color: #28a745;
    font-size: 1em;
  }

  .period-note {
    margin: 5px 0 0 0;
    color: #666;
    font-size: 0.85em;
    font-style: italic;
  }

  .input-container {
    position: relative;
    margin-top: 15px;
  }

  .search-input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
  }

  .search-input:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 2px rgba(250, 180, 90, 0.2);
  }

  .autocomplete-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 6px 6px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    list-style: none;
    padding: 0;
    margin: 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .autocomplete-list li {
    border-bottom: 1px solid #f0f0f0;
  }

  .autocomplete-list li:last-child {
    border-bottom: none;
  }

  .autocomplete-list button {
    width: 100%;
    padding: 12px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .autocomplete-list button:hover {
    background-color: #f8f9fa;
  }

  .student-option {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .student-name {
    font-weight: 500;
    color: #333;
    font-size: 0.9em;
  }

  .student-email {
    color: #666;
    font-size: 0.8em;
  }

  /* Search Type Toggle Styles */
  .search-type-toggle {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
    flex-wrap: wrap;
  }

  .toggle-btn {
    padding: 8px 16px;
    border: 2px solid #28a745;
    background: white;
    color: #28a745;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.9em;
    font-weight: 500;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .toggle-btn:hover {
    background: #f8f9fa;
  }

  .toggle-btn.active {
    background: #28a745;
    color: white;
  }

  /* Phone Search Styles */
  .phone-option {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .phone-number {
    font-weight: 600;
    color: #28a745;
    font-size: 0.95em;
  }

  .phone-student-name {
    font-weight: 500;
    color: #333;
    font-size: 0.9em;
  }

  .phone-student-email {
    color: #666;
    font-size: 0.8em;
  }

  .selected-student {
    margin-top: 20px;
    padding: 15px;
    background-color: #fff3e0;
    border: 2px solid #fab45a;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 15px;
  }

  .selected-student-info h5 {
    margin: 0 0 10px 0;
    color: #fab45a;
    font-size: 1em;
  }

  .selected-student-info p {
    margin: 5px 0;
    font-size: 0.9em;
  }

  .selected-student-info p strong {
    color: #333;
  }

  .btn-clear-small {
    padding: 8px 16px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85em;
    white-space: nowrap;
  }

  .btn-clear-small:hover {
    background-color: #545b62;
  }

  .student-info-container h3 {
    margin: 0 0 20px 0;
    color: #333;
    border-bottom: 2px solid #fab45a;
    padding-bottom: 10px;
  }

  .error-message {
    background-color: #f8d7da;
    color: #721c24;
    padding: 10px;
    border: 1px solid #f5c6cb;
    border-radius: 5px;
    margin-bottom: 20px;
  }

  .loading {
    text-align: center;
    padding: 20px;
    color: #666;
    font-style: italic;
  }

  .student-overview {
    background: #fff;
    border: 2px solid #28a745;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .student-overview h4 {
    margin: 0 0 20px 0;
    color: #28a745;
    font-size: 1.2em;
    border-bottom: 2px solid #28a745;
    padding-bottom: 10px;
  }

  .overview-section {
    margin-bottom: 20px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 6px;
  }

  .overview-section h5 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 1.1em;
  }

  .overview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
  }

  .overview-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    background: white;
    border-radius: 4px;
    border-left: 4px solid #28a745;
  }

  .overview-label {
    font-weight: 600;
    color: #555;
  }

  .overview-value {
    font-weight: bold;
    color: #28a745;
  }

  .transactions-list {
    margin-top: 15px;
  }

  .transactions-list h6 {
    margin: 0 0 10px 0;
    color: #666;
    font-size: 0.9em;
  }

  .transaction-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    background: white;
    border-radius: 4px;
    margin-bottom: 5px;
    border-left: 3px solid #28a745;
  }

  .transaction-amount {
    font-weight: bold;
    color: #28a745;
  }

  .transaction-type {
    color: #666;
    font-size: 0.9em;
  }

  .transaction-date {
    color: #999;
    font-size: 0.8em;
  }

  .teachers-overview {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 10px;
  }

  .teacher-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: white;
    border-radius: 4px;
    border: 1px solid #ddd;
  }

  .teacher-name {
    font-weight: 500;
    color: #333;
  }

  .teacher-details {
    color: #666;
    font-size: 0.8em;
  }

  .subjects-overview {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .subject-tag {
    padding: 6px 12px;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    border-radius: 15px;
    font-size: 0.85em;
    font-weight: 500;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .no-data {
    color: #666;
    font-style: italic;
    margin: 0;
  }

  /* Attendance Details Styles */
  .attendance-details-toggle {
    margin-top: 15px;
    text-align: center;
  }

  .btn-toggle-details {
    padding: 8px 16px;
    background: #28a745;
    color: white;
    border: none;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.9em;
    font-weight: 500;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .btn-toggle-details:hover {
    background: #218838;
    transform: translateY(-1px);
  }

  .attendance-details {
    margin-top: 15px;
    padding: 15px;
    background: white;
    border-radius: 8px;
    border: 1px solid #ddd;
  }

  .attendance-category {
    margin-bottom: 20px;
  }

  .attendance-category:last-child {
    margin-bottom: 0;
  }

  .attendance-category h6 {
    margin: 0 0 12px 0;
    color: #333;
    font-size: 1em;
    font-weight: 600;
    padding-bottom: 8px;
    border-bottom: 2px solid #28a745;
  }

  .attendance-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .attendance-item {
    padding: 12px;
    border-radius: 6px;
    border-left: 4px solid;
  }

  .late-item {
    background: #fff3cd;
    border-left-color: #ffc107;
  }

  .no-show-item {
    background: #f8d7da;
    border-left-color: #dc3545;
  }

  .on-time-item {
    background: #d4edda;
    border-left-color: #28a745;
  }

  .attendance-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .class-name {
    font-weight: 600;
    color: #333;
    flex: 1;
    min-width: 150px;
  }

  .class-teacher {
    color: #666;
    font-size: 0.9em;
    background: #f8f9fa;
    padding: 2px 8px;
    border-radius: 12px;
  }

  .late-badge {
    background: #ffc107;
    color: #000;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 600;
  }

  .no-show-badge {
    background: #dc3545;
    color: white;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 600;
  }

  .on-time-badge {
    background: #28a745;
    color: white;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 600;
  }

  .attendance-times {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .time-info {
    font-size: 0.85em;
    color: #666;
  }

  .time-info strong {
    color: #333;
  }

  .student-comments {
    background: #f8f9fa;
    border: 2px solid #28a745;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .student-comments h4 {
    margin: 0 0 15px 0;
    color: #28a745;
    font-size: 1.2em;
    border-bottom: 2px solid #28a745;
    padding-bottom: 10px;
  }

  .comments-summary {
    margin-bottom: 15px;
    padding: 10px;
    background: white;
    border-radius: 6px;
  }

  .comments-summary p {
    margin: 0;
    color: #666;
  }

  .comments-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .comment-item {
    background: white;
    border-radius: 8px;
    border: 1px solid #ddd;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }

  .comment-header {
    padding: 15px;
    cursor: pointer;
    transition: background-color 0.2s;
    user-select: none;
  }

  .comment-header:hover {
    background-color: #f8f9fa;
  }

  .comment-basic-info {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 8px;
  }

  .comment-class {
    background: #007bff;
    color: white;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 500;
  }

  .comment-subject {
    background: #28a745;
    color: white;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 500;
  }

  .comment-teacher {
    background: #6c757d;
    color: white;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 500;
  }

  .comment-date {
    background: #ffc107;
    color: #000;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: 500;
  }

  .comment-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  .comment-note-preview {
    flex: 1;
    color: #666;
    font-style: italic;
    font-size: 0.9em;
  }

  .expand-indicator {
    color: #28a745;
    font-weight: bold;
    font-size: 1.1em;
  }

  .comment-details {
    padding: 15px;
    background: #f8f9fa;
    border-top: 1px solid #ddd;
  }

  .comment-ratings {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
    margin-bottom: 15px;
  }

  .rating-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: white;
    border-radius: 6px;
    border-left: 3px solid #28a745;
  }

  .rating-label {
    font-weight: 500;
    color: #555;
    font-size: 0.85em;
  }

  .rating-value {
    font-weight: bold;
    color: #28a745;
    font-size: 0.9em;
  }

  .comment-full-note {
    background: white;
    padding: 15px;
    border-radius: 6px;
    border-left: 4px solid #28a745;
  }

  .comment-full-note h6 {
    margin: 0 0 10px 0;
    color: #28a745;
    font-size: 0.9em;
  }

  .comment-full-note p {
    margin: 0;
    color: #333;
    line-height: 1.5;
  }

  .no-comments {
    text-align: center;
    padding: 40px 20px;
    color: #666;
  }

  .no-comments p {
    margin: 0;
    font-style: italic;
  }

  .student-details {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
  }

  .student-details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .student-details h4 {
    margin: 0;
    color: #333;
    font-size: 1.2em;
  }

  .edit-controls {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.9em;
    font-weight: 500;
    transition: all 0.2s;
    text-decoration: none;
    display: inline-block;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background-color: #fab45a;
    color: black;
  }

  .btn-primary:hover:not(:disabled) {
    background-color: #e09a43;
  }

  .btn-secondary {
    background-color: #6c757d;
    color: white;
  }

  .btn-secondary:hover:not(:disabled) {
    background-color: #545b62;
  }

  .info-section {
    margin-bottom: 25px;
    background: white;
    padding: 15px;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .info-section h5 {
    margin: 0 0 15px 0;
    color: #28a745;
    font-size: 1.1em;
    border-bottom: 1px solid #eee;
    padding-bottom: 5px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 15px;
  }

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .info-item:last-child {
    border-bottom: none;
  }

  .label {
    font-weight: 600;
    color: #555;
    min-width: 140px;
  }

  .value {
    color: #333;
    text-align: right;
    flex: 1;
    word-break: break-word;
  }

  .edit-input {
    flex: 1;
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9em;
  }

  .edit-input:focus {
    outline: none;
    border-color: #28a745;
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
  }

  .edit-textarea {
    flex: 1;
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9em;
    resize: vertical;
  }

  .edit-textarea:focus {
    outline: none;
    border-color: #28a745;
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
  }

  /* View All Transactions Button Styles */
  .view-all-transactions-btn-container {
    margin-top: 15px;
    text-align: center;
    padding-top: 10px;
    border-top: 1px solid #eee;
  }

  .btn-view-all-transactions {
    padding: 10px 20px;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    border: none;
    border-radius: 25px;
    cursor: pointer;
    font-size: 0.9em;
    font-weight: 600;
    transition: all 0.3s;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .btn-view-all-transactions:hover:not(:disabled) {
    background: linear-gradient(135deg, #218838, #1e8e7a);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  .btn-view-all-transactions:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 20px;
  }

  .modal-content {
    background: white;
    border-radius: 12px;
    max-width: 800px;
    width: 100%;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0,0,0,0.3);
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    padding: 20px;
    border-bottom: 1px solid #ddd;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8f9fa;
  }

  .modal-header h4 {
    margin: 0;
    color: #28a745;
    font-size: 1.3em;
  }

  .modal-close-btn {
    background: none;
    border: none;
    font-size: 2em;
    color: #666;
    cursor: pointer;
    padding: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s;
  }

  .modal-close-btn:hover {
    background: #f0f0f0;
    color: #333;
  }

  .modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
  }

  .modal-footer {
    padding: 15px 20px;
    border-top: 1px solid #ddd;
    display: flex;
    justify-content: flex-end;
    background: #f8f9fa;
  }

  .transactions-summary {
    margin-bottom: 20px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #28a745;
  }

  .transactions-summary p {
    margin: 0;
    color: #666;
    font-size: 1em;
  }

  .all-transactions-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 400px;
    overflow-y: auto;
  }

  .transaction-item-detailed {
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    transition: all 0.2s;
  }

  .transaction-item-detailed:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    transform: translateY(-1px);
  }

  .transaction-main-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 8px;
  }

  .transaction-amount-detailed {
    font-weight: bold;
    font-size: 1.1em;
    padding: 4px 12px;
    border-radius: 15px;
  }

  .transaction-amount-detailed.payment {
    background: #d4edda;
    color: #155724;
  }

  .transaction-amount-detailed.charge {
    background: #f8d7da;
    color: #721c24;
  }

  .transaction-type-detailed {
    background: #007bff;
    color: white;
    padding: 3px 12px;
    border-radius: 12px;
    font-size: 0.85em;
    font-weight: 500;
    text-transform: capitalize;
  }

  .transaction-date-detailed {
    background: #6c757d;
    color: white;
    padding: 3px 12px;
    border-radius: 12px;
    font-size: 0.85em;
    font-weight: 500;
  }

  .transaction-description {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #eee;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .description-label {
    font-weight: 600;
    color: #555;
    font-size: 0.85em;
  }

  .description-text {
    color: #333;
    font-size: 0.9em;
    line-height: 1.4;
  }

  .no-transactions {
    text-align: center;
    padding: 40px 20px;
    color: #666;
  }

  .no-transactions p {
    margin: 0;
    font-style: italic;
  }

  @media (max-width: 768px) {
    .student-info-container {
      padding: 15px;
    }

    .month-selection {
      flex-direction: column;
      align-items: stretch;
      gap: 15px;
    }

    .month-selector-container {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
    }

    .month-select,
    .year-select {
      width: 100%;
    }

    .selected-period {
      margin-left: 0;
      justify-content: center;
    }

    .overview-grid {
      grid-template-columns: 1fr;
    }

    .overview-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
    }

    .teachers-overview {
      grid-template-columns: 1fr;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .info-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
    }

    .label {
      min-width: auto;
    }

    .value {
      text-align: left;
    }

    .subjects-overview {
      justify-content: center;
    }

    .comment-basic-info {
      justify-content: center;
    }

    .comment-ratings {
      grid-template-columns: 1fr;
    }

    .comment-summary {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .search-type-toggle {
      justify-content: center;
    }

    .toggle-btn {
      flex: 1;
      min-width: 120px;
      justify-content: center;
    }

    .attendance-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .class-name {
      min-width: auto;
    }

    .attendance-times {
      width: 100%;
    }

    .modal-content {
      margin: 10px;
      max-height: 95vh;
    }

    .modal-header h4 {
      font-size: 1.1em;
    }

    .transaction-main-info {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .btn-view-all-transactions {
      font-size: 0.8em;
      padding: 8px 16px;
    }

    .view-all-transactions-btn-container {
      margin-top: 10px;
    }

    .all-transactions-list {
      max-height: 300px;
    }

    .transaction-item-detailed {
      padding: 12px;
    }
  }
</style>
